import React, { useState, useEffect } from "react";
import {
  Panel,
  PanelType,
  PrimaryButton,
  DefaultButton,
  Checkbox,
  ChoiceGroup,
  IChoiceGroupOption,
  DatePicker,
  Text,
  Stack,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import PanelActions from "components/shared/panel/PanelActions";

interface PolicyPublishDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  onPublish: (publishData: IPolicyPublishData) => void;
  policyName: string;
  templateName: string;
  initialContentRefreshDate?: string;
  initialAgreementEffectiveDate?: string;
  initialAgreementSettings?: 'entire' | 'updated';
}

export interface IPolicyPublishData {
  contentRefreshDate?: Date;
  agreementEffectiveDate?: Date;
  agreementSettings: "entire" | "updated";
  signatureBlocks: "none" | "partner" | "customer";
  isInternalTestOnly: boolean;
}

function PolicyPublishDialog({
  isOpen,
  onDismiss,
  onPublish,
  policyName,
  templateName,
  initialContentRefreshDate,
  initialAgreementEffectiveDate,
  initialAgreementSettings,
}: PolicyPublishDialogProps) {
  const [contentRefreshDate, setContentRefreshDate] = useState<Date | undefined>(
    initialContentRefreshDate ? new Date(initialContentRefreshDate) : undefined
  );
  const [agreementEffectiveDate, setAgreementEffectiveDate] = useState<Date | undefined>(
    initialAgreementEffectiveDate ? new Date(initialAgreementEffectiveDate) : undefined
  );
  const [agreementSettings, setAgreementSettings] = useState<"entire" | "updated">(
    initialAgreementSettings || "entire"
  );
  
  // Update state when initial values change (when dialog opens with new data)
  useEffect(() => {
    if (isOpen) {
      setContentRefreshDate(initialContentRefreshDate ? new Date(initialContentRefreshDate) : undefined);
      setAgreementEffectiveDate(initialAgreementEffectiveDate ? new Date(initialAgreementEffectiveDate) : undefined);
      setAgreementSettings(initialAgreementSettings || "entire");
    }
  }, [isOpen, initialContentRefreshDate, initialAgreementEffectiveDate, initialAgreementSettings]);
  const [signatureBlocks] = useState<"none" | "partner" | "customer">("none");
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [isInternalTestOnly, setIsInternalTestOnly] = useState<boolean>(false);

  // Get today's date at midnight for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Disable past dates for effective date - only allow current date and future dates
  const minDate = today;

  // Content refresh date: allow past dates and today, but not future dates
  const maxRefreshDate = new Date(today);

  const handleRefreshDateChange = (date: Date | null | undefined) => {
    setContentRefreshDate(date || undefined);
  };

  const handleEffectiveDateChange = (date: Date | null | undefined) => {
    setAgreementEffectiveDate(date || undefined);
  };

  const agreementSettingsOptions: IChoiceGroupOption[] = [
    { key: "entire", text: "Generate entire agreement" },
    { key: "updated", text: "Generate updated terms only" },
  ];

  // TODO: signatureBlocksOptions will be used when signature block UI is implemented
  // const signatureBlocksOptions: IChoiceGroupOption[] = [
  //   { key: "none", text: "None" },
  //   { key: "partner", text: "Include partner signature" },
  //   { key: "customer", text: "Include customer signature" },
  // ];

  const handlePublish = () => {
    setIsPublishing(true);
    onPublish({
      contentRefreshDate,
      agreementEffectiveDate,
      agreementSettings,
      signatureBlocks,
      isInternalTestOnly,
    });
  };

  const renderFooter = () => (
    <PanelActions>
      <Checkbox
        label="Publish for internal testing purpose only (not applicable for creating live customer agreements)"
        checked={isInternalTestOnly}
        onChange={(_, checked) => setIsInternalTestOnly(!!checked)}
        data-automation-id="policy-publish-internal-test-checkbox"
        styles={{ root: { marginBottom: 12 } }}
      />
      <PrimaryButton
        onClick={handlePublish}
        data-automation-id="policy-publish-confirm-button"
        disabled={isPublishing || !contentRefreshDate || !agreementEffectiveDate}
        styles={{
          root: {
            marginRight: "8px",
          },
        }}
      >
        {isPublishing ? (
          <>
            <Spinner size={SpinnerSize.xSmall} styles={{ root: { marginRight: 8 } }} />
            Publishing
          </>
        ) : (
          "Publish"
        )}
      </PrimaryButton>
      <DefaultButton
        onClick={onDismiss}
        text="Cancel"
        data-automation-id="policy-publish-cancel-button"
        disabled={isPublishing}
      />
    </PanelActions>
  );

  return (
    <Panel
      data-automation-id="policy-publish-panel"
      headerText="Are you sure you want to publish?"
      type={PanelType.custom}
      customWidth="600px"
      isOpen={isOpen}
      onDismiss={onDismiss}
      isLightDismiss={false}
      onRenderFooter={renderFooter}
      isFooterAtBottom
      styles={{
        main: {
          zIndex: 1000,
        },
        overlay: {
          zIndex: 999,
        },
      }}
    >
      <div style={{ padding: "20px 0" }}>
        <Stack tokens={{ childrenGap: 20 }}>
          <Text
            variant="small"
            style={{
              color: "#605E5C",
              fontSize: "14px",
              lineHeight: "20px",
            }}
          >
            Once published, the policy will be live right away. If you need to make changes or withdraw the
            policy, you can do so any time before the policy effective date you specify below.
          </Text>

          {/* Policy name - read-only */}
          <Stack tokens={{ childrenGap: 6 }}>
            <Text
              style={{
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#323130",
              }}
            >
              Policy name
            </Text>
            <Text
              style={{
                color: "#323130",
                fontSize: "14px",
                lineHeight: "20px",
              }}
            >
              {policyName}
            </Text>
          </Stack>

          {/* Template applicable - read-only */}
          <Stack tokens={{ childrenGap: 6 }}>
            <Text
              style={{
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#323130",
              }}
            >
              Template applicable
            </Text>
            <Text
              style={{
                color: "#323130",
                fontSize: "14px",
                lineHeight: "20px",
              }}
            >
              {templateName}
            </Text>
          </Stack>

          {/* Content refresh date */}
          <DatePicker
            label="Content refresh date *"
            placeholder="Day, Month dd, yyyy"
            ariaLabel="Select content refresh date"
            value={contentRefreshDate}
            onSelectDate={handleRefreshDateChange}
            maxDate={maxRefreshDate}
            styles={{
              root: { maxWidth: "100%" },
              wrapper: { marginTop: "4px" },
            }}
          />

          {/* Agreement effective date */}
          <Stack tokens={{ childrenGap: 4 }}>
            <DatePicker
              label="Agreement effective date *"
              placeholder="Day, Month dd, yyyy"
              ariaLabel="Select agreement effective date"
              value={agreementEffectiveDate}
              onSelectDate={handleEffectiveDateChange}
              minDate={minDate}
              styles={{
                root: { maxWidth: "100%" },
                wrapper: { marginTop: "4px" },
              }}
            />
          </Stack>

          {/* Agreement settings */}
          <Stack tokens={{ childrenGap: 12 }}>
            <Text
              style={{
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#323130",
              }}
            >
              Agreement settings
            </Text>
            <ChoiceGroup
              selectedKey={agreementSettings}
              options={agreementSettingsOptions}
              onChange={(_, option) => setAgreementSettings(option?.key as "entire" | "updated")}
              styles={{
                flexContainer: {
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                },
              }}
            />
          </Stack>

          {/* Signature blocks required */}
          {/* <Stack tokens={{ childrenGap: 12 }}>
            <Text 
              style={{ 
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#323130",
              }}
            >
              Signature blocks required
            </Text>
            <Text 
              style={{ 
                color: "#605E5C",
                fontSize: "12px",
                lineHeight: "16px",
                fontStyle: "italic",
              }}
            >
              Please note the Microsoft signature block is automatically included in all generated agreements.
            </Text>
            <ChoiceGroup
              selectedKey={signatureBlocks}
              options={signatureBlocksOptions}
              onChange={(_, option) => setSignatureBlocks(option?.key as "none" | "partner" | "customer")}
              styles={{
                flexContainer: {
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                },
              }}
            />
          </Stack> */}
        </Stack>
      </div>
    </Panel>
  );
}

export default PolicyPublishDialog;
